import React from 'react';

/**
 * ConfidenceIndicator - Displays evaluation confidence score with visual progress
 * 
 * @param {number} confidenceScore - Score from 0.0 to 1.0
 * @param {string} confidenceLevel - Level: 'high', 'medium', 'low'
 * @param {string} size - Size: 'sm', 'md', 'lg'
 * @param {boolean} showLabel - Whether to show text label
 */
export default function ConfidenceIndicator({ confidenceScore, confidenceLevel, size = 'md', showLabel = true }) {
    if (confidenceScore === null || confidenceScore === undefined) {
        return null;
    }

    const percentage = Math.round(confidenceScore * 100);

    // Get color based on confidence level
    const getColorClasses = (level) => {
        switch (level) {
            case 'high':
                return {
                    bg: 'bg-green-100',
                    fill: 'bg-green-500',
                    text: 'text-green-700',
                    label: 'High Confidence'
                };
            case 'medium':
                return {
                    bg: 'bg-yellow-100',
                    fill: 'bg-yellow-500',
                    text: 'text-yellow-700',
                    label: 'Medium Confidence'
                };
            case 'low':
                return {
                    bg: 'bg-red-100',
                    fill: 'bg-red-500',
                    text: 'text-red-700',
                    label: 'Low Confidence'
                };
            default:
                return {
                    bg: 'bg-secondary-100',
                    fill: 'bg-secondary-500',
                    text: 'text-secondary-700',
                    label: 'Unknown'
                };
        }
    };

    const sizeClasses = {
        sm: {
            container: 'w-24',
            height: 'h-1.5',
            text: 'text-xs',
            gap: 'gap-1'
        },
        md: {
            container: 'w-32',
            height: 'h-2',
            text: 'text-sm',
            gap: 'gap-2'
        },
        lg: {
            container: 'w-40',
            height: 'h-2.5',
            text: 'text-base',
            gap: 'gap-2'
        }
    };

    const colors = getColorClasses(confidenceLevel);
    const sizes = sizeClasses[size];

    return (
        <div className={`confidence-indicator flex items-center ${sizes.gap}`}>
            {/* Progress bar */}
            <div className={`relative ${sizes.container} ${sizes.height} ${colors.bg} rounded-full overflow-hidden`}>
                <div
                    className={`absolute top-0 left-0 h-full ${colors.fill} transition-all duration-500`}
                    style={{ width: `${percentage}%` }}
                ></div>
            </div>

            {/* Label */}
            {showLabel && (
                <span className={`font-semibold ${colors.text} ${sizes.text}`}>
                    {percentage}%
                </span>
            )}

            {/* Tooltip on hover */}
            <span className={` ${sizes.text} ${colors.text} opacity-70`}>
                ({colors.label})
            </span>
        </div>
    );
}
